<script setup lang="ts">
/**
 * "เติมจากแชททั้งหมด" at the top of the order tab (owner 18 ต.ค. 69 "สมมุติแชทนั้นคนตอบ 100% ทำไงให้ลงข้อมูลครบเหมือนใช้ AI
 * ตอบ"): the AI reads the whole chat the staff answer themselves and fills the EMPTY fields of the form (stores/script.ts
 * readChat) — then "AI อ่านแชทแล้ว · ยังขาด: …" and what to check. Hidden in an AI-mode chat (the tab fills from the AI
 * session there), a comment thread or a group. Nothing is saved; the staff check and press บันทึก.
 * การสั่งครั้งนี้ (owner 18 ต.ค. 69 "ระวังเวลาลูกค้าเก่าสั่งซื้อใหม่ ไม่ใช่ไปอ่านออเดอร์เก่ามานะ"): a chat with an earlier order
 * (or a long silence) is read only after it — the hint says so before, the read's own line after ("อ่านเฉพาะแชทตั้งแต่ …").
 */
import { computed } from 'vue'
import { useScript } from '../../../stores/script'
import Icon from '../../ui/Icon.vue'

const props = defineProps<{ convId: string }>()
const script = useScript()

const show = computed(() => script.readable(props.convId))
const busy = computed(() => !!script.reading[props.convId])
const read = computed(() => script.reads[props.convId] ?? null)
const err = computed(() => script.readErrors[props.convId] ?? null)
/** The chat has an earlier purchase (the script's boundary): the AI reads only the one talked about now. */
const hint = computed(() =>
  script.scripts[props.convId]?.session
    ? 'AI อ่านเฉพาะแชทของการสั่งครั้งนี้ เติมเฉพาะช่องที่ยังว่าง'
    : 'AI อ่านทั้งแชท เติมเฉพาะช่องที่ยังว่าง',
)
const since = computed(() => read.value?.session?.label ?? null)
const status = computed(() => {
  const r = read.value
  if (!r) return ''
  return `AI อ่านแชทแล้ว · ${r.missing.length ? `ยังขาด: ${r.missing.join(', ')}` : 'ข้อมูลครบตามแชท'}`
})
</script>

<template>
  <section v-if="show" class="cr" aria-label="เติมจากแชททั้งหมด">
    <div class="cr-row">
      <button type="button" class="btn2 cr-b" :disabled="busy" :aria-busy="busy" @click="script.readChat(convId)">
        <span v-if="busy" class="spin" aria-hidden="true"></span><Icon v-else name="sparkles" />{{
          busy ? 'AI กำลังอ่านแชท…' : 'เติมจากแชททั้งหมด'
        }}
      </button>
      <span class="cr-h">{{ hint }}</span>
    </div>
    <p v-if="err" class="err" role="alert"><Icon name="alertCircle" /><span>{{ err }}</span></p>
    <template v-else-if="read">
      <p class="cr-st" role="status"><Icon name="checks" /><span>{{ status }}</span></p>
      <p v-if="since" class="cr-since"><Icon name="history" /><span>{{ since }}</span></p>
      <p v-if="!read.filled.length" class="note">ช่องที่เลือกไว้แล้วไม่ได้เปลี่ยน</p>
      <ul v-if="read.notes.length" class="src-notes" aria-label="ข้อควรเช็คจาก AI">
        <li v-for="n in read.notes" :key="n"><Icon name="info" /><span>{{ n }}</span></li>
      </ul>
    </template>
  </section>
</template>

<style scoped>
.cr {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #f7faf8;
}
.cr-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 10px;
}
.ord .cr-b {
  height: 36px;
  border-color: #bfdccd;
  color: var(--ac2);
  font-weight: 600;
}
.ord .cr-b .ic {
  color: var(--ac);
}
.ord .cr-b:disabled {
  opacity: 0.75;
  cursor: progress;
}
.cr-h {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.cr-st {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 0;
  font-size: 12.5px;
  line-height: 19px;
  font-weight: 600;
  color: #3e4d46;
  overflow-wrap: anywhere;
}
.cr-st .ic {
  flex: none;
  width: 14px;
  height: 14px;
  margin-top: 2.5px;
  color: var(--ac);
}
.cr-since {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
  overflow-wrap: anywhere;
}
.cr-since .ic {
  flex: none;
  width: 13px;
  height: 13px;
  margin-top: 2.5px;
}
.cr .note,
.cr .err,
.cr .src-notes {
  margin: 0;
}
</style>
